import { useCallback, useEffect, useRef, useState } from "react";
import type { CreditPlan } from "../../../../../packages/domain/src/billing.ts";
import { Icon } from "../../app/Icon";
import { createBillingCheckout, navigateToCheckout } from "./billingClient";
import { useAnalytics } from "../analytics/analytics";
export function CheckoutButton({ selection, upgrade = false, label, onStart, autoStart = false }: { selection: CreditPlan; upgrade?: boolean; label?: string; onStart?: () => void; autoStart?: boolean }) {
  const analytics = useAnalytics();
  const [key] = useState(() => crypto.randomUUID());
  const request = useRef<AbortController | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => () => request.current?.abort(), []);
  const { tier, interval, artists } = selection;
  const checkout = useCallback(async () => {
    if (request.current) return;
    const controller = new AbortController(); request.current = controller; setBusy(true); setError("");
    onStart?.();
    analytics.track("checkout_started", { plan: tier, interval, artists, upgrade });
    try {
      const url = await createBillingCheckout({ tier, interval, artists }, key, AbortSignal.any([controller.signal, AbortSignal.timeout(20_000)]), upgrade);
      if (!controller.signal.aborted) {
        analytics.track("checkout_opened", { plan: tier, interval, artists, upgrade });
        navigateToCheckout(url);
      }
    } catch (failure) {
      if (!controller.signal.aborted) {
        analytics.track("checkout_failed", { plan: tier, interval, upgrade });
        setBusy(false);
        setError(failure instanceof Error ? failure.message : "Checkout could not be opened. Please try again.");
      }
    } finally { if (!controller.signal.aborted) { request.current = null; } }
  }, [analytics, artists, interval, key, onStart, tier, upgrade]);
  const automaticStarted = useRef(false);
  useEffect(() => {
    if (!autoStart || automaticStarted.current) return;
    // Defer until after effect setup so Strict Mode cleanup cannot cancel the only attempt.
    const timer = window.setTimeout(() => {
      automaticStarted.current = true;
      void checkout();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [autoStart, checkout]);
  if (autoStart && !error) return <p role="status">Opening secure checkout…</p>;
  return <>{error && <p className="billing-unavailable" role="alert">{error}</p>}<button type="button" className="billing-cta" disabled={busy} onClick={() => void checkout()}>{busy ? "Opening secure checkout…" : label ?? (upgrade ? "Pay full price and upgrade" : "Continue to secure checkout")}<Icon name="arrow-right" /></button></>;
}
